/**
 * FirmenDomains - the D2 multi-domain surface on the Kunde detail page.
 *
 * "Eine Firma kann mehrere Domains haben" (build plan Part 2B / D2). A firm matches
 * inbound mail on ALL of its domains, and the system already collects them from three
 * directions (intake genesis, the resolution cache, the Chrome extension) plus the one
 * a broker may type at create time. Until D2 none of them were visible anywhere, and a
 * wrong mapping silently mis-routed every future mail from that domain.
 *
 * Firma-only: email_domain.firma_id is a firma FK, so the card is not rendered for a
 * natürliche Person. Live-only: in mock mode there is no backend to read.
 *
 * Removal is SOFT on the api side (status becomes 'rejected'), so a removed domain
 * stays in the list, greyed out, with a "Reaktivieren" action. That is deliberate: a
 * hard delete would be re-learned by the next inbound mail from that domain.
 *
 * [Source: apps/web/src/features/entity-detail/components/ZugriffFreigabe.tsx] (live-only tab surface)
 * [Source: apps/web/src/features/administration/pages/MandantPage.tsx] (domain chip editor)
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AtSign, Plus, RotateCcw, X } from 'lucide-react';
// The ONE normaliser, shared with the api so the value the broker is shown here is
// byte-identical to the one stored as the email_domain key.
import { normalizeDomain } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { apiClient } from '../../../lib/api-client';
import type { FirmDomainItem } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';

interface Props {
  firmaId: string;
  /** false renders the list read-only (no add field, no remove/restore buttons). */
  canManage: boolean;
}

/** Catches a typo before the round trip; the api is still the authority. */
const DOMAIN_PATTERN = /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

export function FirmenDomains({ firmaId, canManage }: Props) {
  const live = isLiveData();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.firmDomains(firmaId),
    queryFn: () => apiClient.firmDomains.list(firmaId),
    enabled: live,
    retry: false,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.firmDomains(firmaId) });

  const add = useMutation({
    mutationFn: (domain: string) => apiClient.firmDomains.add(firmaId, domain),
    onSuccess: () => {
      setError(null);
      setDraft('');
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (domain: string) => apiClient.firmDomains.remove(firmaId, domain),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (!live) return null;

  const domains: FirmDomainItem[] = data?.domains ?? [];
  const normalized = normalizeDomain(draft);
  const draftValid = normalized !== '' && DOMAIN_PATTERN.test(normalized);
  const busy = add.isPending || remove.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!draftValid || busy) return;
    add.mutate(normalized);
  }

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <AtSign className="size-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">E-Mail-Domains</h3>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <p className="text-xs text-muted-foreground">
          Eingehende E-Mails von diesen Domains werden diesem Kunden zugeordnet. Eine Domain
          kann immer nur einem Kunden gehören. Öffentliche Anbieter wie gmail.com werden nie
          zugeordnet.
        </p>

        {isLoading ? (
          <span className="text-sm text-muted-foreground">Lade Domains …</span>
        ) : isError ? (
          <span className="text-sm text-status-danger">Domains konnten nicht geladen werden.</span>
        ) : domains.length === 0 ? (
          <span className="text-sm text-muted-foreground">Keine Domains hinterlegt.</span>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {domains.map((d) => {
              const removed = d.status === 'rejected';
              return (
                <li
                  key={d.id}
                  className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs ${
                    removed
                      ? 'border-dashed border-border bg-transparent text-muted-foreground'
                      : 'border-border bg-muted text-foreground'
                  }`}
                >
                  <span className={`font-mono ${removed ? 'line-through' : ''}`}>{d.domain}</span>
                  {removed && <span className="text-[10px] uppercase">deaktiviert</span>}
                  {canManage &&
                    (removed ? (
                      <button
                        type="button"
                        aria-label={`${d.domain} reaktivieren`}
                        disabled={busy}
                        onClick={() => add.mutate(d.domain)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <RotateCcw className="size-3" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        aria-label={`${d.domain} entfernen`}
                        disabled={busy}
                        onClick={() => remove.mutate(d.domain)}
                        className="text-muted-foreground hover:text-status-danger"
                      >
                        <X className="size-3" />
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>
        )}

        {error !== null && <p className="text-sm text-status-danger">{error}</p>}

        {canManage && (
          <form onSubmit={submit} className="flex items-center gap-2">
            <label htmlFor="firm-domain-neu" className="sr-only">
              Neue Domain
            </label>
            <input
              id="firm-domain-neu"
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="kunde.ch"
              aria-invalid={draft.trim() !== '' && !draftValid}
              className="h-9 max-w-xs rounded-md border border-border bg-background px-2.5 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <Button type="submit" variant="outline" size="sm" disabled={!draftValid || busy}>
              <Plus className="size-4" /> Hinzufügen
            </Button>
          </form>
        )}
        {canManage && draft.trim() !== '' && !draftValid && (
          <p className="text-xs text-status-danger">Bitte eine gültige Domain angeben.</p>
        )}
      </div>
    </div>
  );
}
